import type { NavItem } from '../components/BottomNav'
import {
  IconCalendarMonth,
  IconDashboard,
  IconEventAvailable,
  IconFitnessCenter,
  IconFlag,
  IconHistory,
  IconLocalFireDepartment,
  IconMenuBook,
  IconMoreHoriz,
  IconNutrition,
  IconPhotoLibrary,
  IconRestaurant,
  IconSettings,
  IconStorage,
  IconTrendingUp,
} from '../components/icons'

/**
 * The web's information architecture (UX spec §2.4–2.5): settings groups, library tabs and the old paths that now
 * redirect, kept apart from the router so they can be tested and so the nav and the pages agree on the names.
 */

export const SETTINGS_GROUPS = [
  { id: 'profile', label: 'Profile & goals' },
  { id: 'nutrition', label: 'Nutrition & coaching' },
  { id: 'training', label: 'Training' },
  { id: 'data', label: 'Data & integrations' },
  { id: 'app', label: 'App' },
  { id: 'advanced', label: 'Advanced' },
] as const
export type SettingsGroup = (typeof SETTINGS_GROUPS)[number]['id']

export function isSettingsGroup(value: string): value is SettingsGroup {
  return SETTINGS_GROUPS.some((group) => group.id === value)
}

export const LIBRARY_TABS = [
  { id: 'foods', label: 'Foods' },
  { id: 'custom', label: 'Custom foods' },
  { id: 'recipes', label: 'Recipes' },
  { id: 'meals', label: 'Meals' },
] as const
export type LibraryTab = (typeof LIBRARY_TABS)[number]['id']

/** The `tab` search param, defaulting to Foods for a missing or unknown value. */
export function parseLibraryTab(value: unknown): LibraryTab {
  return LIBRARY_TABS.find((tab) => tab.id === value)?.id ?? 'foods'
}

/** Old pages that moved into the Library, so bookmarks keep working. */
export const LIBRARY_REDIRECTS = {
  '/foods': 'foods',
  '/custom-foods': 'custom',
  '/recipes': 'recipes',
  '/meals': 'meals',
} as const satisfies Readonly<Record<string, LibraryTab>>

/** The id in an editor URL that means "a new one" rather than a saved item. */
export const NEW_ID = 'new'

/**
 * Whether a nav link is the current page: an exact match or a page under it (`/library/recipes/…` under `/library`,
 * `/settings/nutrition` under `/settings`), but not a sibling that only shares a prefix (`/log` vs `/logout`).
 */
export function isActivePath(pathname: string, to: string): boolean {
  return pathname === to || pathname.startsWith(`${to}/`)
}

/**
 * The sidebar/rail mirrors the phone's tabs (UX spec §2.4): Today, Strategy, Progress, Food, Train; Settings sits at
 * the rail's foot (`Layout.tsx`). The same groups drive the "More" page (`MorePage.tsx`), the bottom nav's 5th
 * destination on phones, so the rail and More never drift apart.
 */
export const NAV_SECTIONS: ReadonlyArray<{ label: string; items: readonly NavItem[] }> = [
  {
    label: 'Today',
    items: [
      { to: '/dashboard', label: 'Dashboard', icon: IconDashboard },
      { to: '/log', label: 'Food log', icon: IconRestaurant },
    ],
  },
  {
    label: 'Strategy',
    items: [
      { to: '/strategy', label: 'Strategy', icon: IconFlag },
      { to: '/check-in', label: 'Check-in', icon: IconEventAvailable },
    ],
  },
  {
    label: 'Progress',
    items: [
      { to: '/weight', label: 'Weight trend', icon: IconTrendingUp },
      { to: '/expenditure', label: 'Expenditure', icon: IconLocalFireDepartment },
      { to: '/nutrients', label: 'Nutrients', icon: IconNutrition },
      { to: '/photos', label: 'Progress photos', icon: IconPhotoLibrary },
      { to: '/history', label: 'History', icon: IconHistory },
    ],
  },
  { label: 'Food', items: [{ to: '/library', label: 'Library', icon: IconMenuBook }] },
  {
    label: 'Train',
    items: [
      { to: '/workouts', label: 'Workouts', icon: IconFitnessCenter },
      { to: '/programs', label: 'Training programs', icon: IconCalendarMonth },
      { to: '/gyms', label: 'Your equipment', icon: IconStorage },
    ],
  },
]

export const SETTINGS_NAV_ITEM: NavItem = { to: '/settings', label: 'Settings', icon: IconSettings }

/**
 * The phone's bottom bar (`MtApp.kt`'s `LeftTabs`/`RightTabs`) plus a 5th "More" destination (owner device test,
 * 24 Oct 2026): the web's compact top bar has no per-tab More button to reach the rest from (unlike the phone's
 * `MoreScreen.kt`), so More sits in the bar instead.
 */
export const BOTTOM_NAV_ITEMS: readonly NavItem[] = [
  { to: '/dashboard', label: 'Dashboard', icon: IconDashboard },
  { to: '/log', label: 'Food Log', icon: IconRestaurant },
  { to: '/workouts', label: 'Train', icon: IconFitnessCenter },
  { to: '/strategy', label: 'Strategy', icon: IconFlag },
  { to: '/more', label: 'More', icon: IconMoreHoriz },
]

const BOTTOM_NAV_DESTINATIONS = new Set<string>(BOTTOM_NAV_ITEMS.slice(0, -1).map((item) => item.to))

/** Everything the bottom nav's four tabs don't already cover, grouped like the rail (`MorePage.tsx`). */
export const MORE_SECTIONS: ReadonlyArray<{ label: string; items: readonly NavItem[] }> = [
  ...NAV_SECTIONS.map((section) => ({
    label: section.label,
    items: section.items.filter((item) => !BOTTOM_NAV_DESTINATIONS.has(item.to)),
  })).filter((section) => section.items.length > 0),
  { label: 'Settings', items: [SETTINGS_NAV_ITEM] },
]

/** The bottom nav's own left-to-right order (compact windows). */
export const BOTTOM_NAV_ORDER: readonly string[] = BOTTOM_NAV_ITEMS.map((item) => item.to)

/** The rail's own top-to-bottom order (600 px and up): every section in order, Settings last at the rail's foot. */
export const RAIL_ORDER: readonly string[] = [...NAV_SECTIONS.flatMap((section) => section.items.map((item) => item.to)), SETTINGS_NAV_ITEM.to]

export type NavDirection = 'forward' | 'backward' | 'none'

/**
 * The slide direction for a route change (owner override, 24 Oct 2026, replacing fade-through; `router.tsx`'s
 * `defaultViewTransition`, styles.css's `:active-view-transition-type()` rules). [order] is whichever nav surface
 * is current — `BOTTOM_NAV_ORDER` on phones, `RAIL_ORDER` from 600 px — since the same pair of routes can sit in a
 * different relative order on each (Strategy comes before Train in the rail's sections but after it in the bottom
 * bar). A destination switch slides by its position in that order: further along slides in from the right/below,
 * earlier from the left/above. Anything else is "deeper" navigation, judged by path containment: a page nested
 * under the current one is forward (right), returning to an ancestor is backward (left, "back reversing the
 * direction") — an unrelated jump (neither a surface switch nor an ancestor/descendant) defaults forward.
 */
export function navDirection(order: readonly string[], from: string, to: string): NavDirection {
  if (from === to) return 'none'
  const fromIndex = order.indexOf(from)
  const toIndex = order.indexOf(to)
  if (fromIndex !== -1 && toIndex !== -1) return toIndex > fromIndex ? 'forward' : 'backward'
  if (from.startsWith(`${to}/`)) return 'backward'
  return 'forward'
}
